import {
  AccessibilityHelp,
  Alignment,
  AutoImage,
  AutoLink,
  Autoformat,
  Autosave,
  // BlockQuote,
  Bold,
  CloudServices,
  Code,
  // CodeBlock,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  // FontSize,
  GeneralHtmlSupport,
  Heading,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  ShowBlocks,
  List,
  // MediaEmbed,
  Paragraph,
  PasteFromOffice,
  SelectAll,
  SourceEditing,
  // SpecialCharacters,
  Strikethrough,
  Style,
  Superscript,
  // Table,
  // TableCaption,
  // TableCellProperties,
  // TableColumnResize,
  // TableProperties,
  // TableToolbar,
  // TextPartLanguage,
  TextTransformation,
  Underline,
  Undo,
} from "ckeditor5";
import { APIUrl } from "../../../../utils/API/fetchBaseQuery";


function findImgViewElement(viewElement) {
  if (viewElement.is('element', 'img')) {
    return viewElement;
  }
  for (const child of viewElement.getChildren()) {
    if (child.is('element')) {
      if (child.is('element', 'img')) {
        return child;
      }
      const img = findImgViewElement(child);
      if (img) {
        return img;
      }
    }
  }
  return null;
}

function customImageResizePlugin(editor) {
  // Upcast: view -> model
  editor.conversion.for('upcast').attributeToAttribute({
    view: {
      name: 'img',
      styles: {
        width: /.+/
      }
    },
    model: 'resizedWidth',
    converterPriority: 'high'
  });

  editor.conversion.for('upcast').attributeToAttribute({
    view: {
      name: 'figure',
      styles: {
        width: /.+/
      }
    },
    model: 'resizedWidth',
    converterPriority: 'high'
  });

  editor.conversion.for('upcast').attributeToAttribute({
    view: {
      name: 'span',
      styles: {
        width: /.+/
      }
    },
    model: 'resizedWidth',
    converterPriority: 'high'
  });

  editor.conversion.for('upcast').attributeToAttribute({
    view: {
      name: 'img',
      attributes: {
        width: /.+/
      }
    },
    model: 'resizedWidth',
    converterPriority: 'high'
  });

  // Data Downcast: model -> view (getData() output only)
  editor.conversion.for('dataDowncast').add(dispatcher => {
    // 1. Unconditionally consume aspectRatio for images to strip aspect-ratio style
    ['imageBlock', 'imageInline'].forEach(elementType => {
      dispatcher.on(`attribute:aspectRatio:${elementType}`, (evt, data, conversionApi) => {
        conversionApi.consumable.consume(data.item, evt.name);
      }, { priority: 'high' });
    });

    // 2. Consume width and height if resizedWidth is present to avoid HTML width/height attributes
    ['width', 'height'].forEach(attrName => {
      ['imageBlock', 'imageInline'].forEach(elementType => {
        dispatcher.on(`attribute:${attrName}:${elementType}`, (evt, data, conversionApi) => {
          if (data.item.hasAttribute('resizedWidth')) {
            conversionApi.consumable.consume(data.item, evt.name);
          }
        }, { priority: 'high' });
      });
    });

    // 3. Custom downcast of resizedWidth to <img> HTML attribute
    dispatcher.on('attribute:resizedWidth:imageBlock', (evt, data, conversionApi) => {
      if (!conversionApi.consumable.consume(data.item, evt.name)) {
        return;
      }

      const viewWriter = conversionApi.writer;
      const figure = conversionApi.mapper.toViewElement(data.item);
      
      if (figure) {
        // Remove style width from figure container
        viewWriter.removeStyle('width', figure);
        viewWriter.removeStyle('aspect-ratio', figure);
        
        // Find <img> inside figure
        const img = findImgViewElement(figure);
        if (img) {
          if (data.attributeNewValue) {
            const widthVal = data.attributeNewValue;
            const match = widthVal.match(/^(\d+)(px)?$/);
            if (match) {
              const numericWidth = match[1];
              viewWriter.setAttribute('width', numericWidth, img);
              viewWriter.setStyle('height', 'auto', img);
              viewWriter.removeStyle('width', img);
              viewWriter.removeStyle('aspect-ratio', img);
              viewWriter.removeAttribute('height', img);
            } else {
              viewWriter.removeAttribute('width', img);
              viewWriter.removeStyle('width', img);
              viewWriter.removeStyle('height', img);
              viewWriter.removeStyle('aspect-ratio', img);
              viewWriter.removeAttribute('height', img);
            }
          } else {
            viewWriter.removeAttribute('width', img);
            viewWriter.removeStyle('width', img);
            viewWriter.removeStyle('height', img);
            viewWriter.removeStyle('aspect-ratio', img);
            viewWriter.removeAttribute('height', img);
          }
        }
      }
    }, { priority: 'high' });

    dispatcher.on('attribute:resizedWidth:imageInline', (evt, data, conversionApi) => {
      if (!conversionApi.consumable.consume(data.item, evt.name)) {
        return;
      }

      const viewWriter = conversionApi.writer;
      const span = conversionApi.mapper.toViewElement(data.item);
      
      if (span) {
        // Remove style width from span container
        viewWriter.removeStyle('width', span);
        viewWriter.removeStyle('aspect-ratio', span);
        
        // Find <img> inside span
        const img = findImgViewElement(span);
        if (img) {
          if (data.attributeNewValue) {
            const widthVal = data.attributeNewValue;
            const match = widthVal.match(/^(\d+)(px)?$/);
            if (match) {
              const numericWidth = match[1];
              viewWriter.setAttribute('width', numericWidth, img);
              viewWriter.setStyle('height', 'auto', img);
              viewWriter.removeStyle('width', img);
              viewWriter.removeStyle('aspect-ratio', img);
              viewWriter.removeAttribute('height', img);
            } else {
              viewWriter.removeAttribute('width', img);
              viewWriter.removeStyle('width', img);
              viewWriter.removeStyle('height', img);
              viewWriter.removeStyle('aspect-ratio', img);
              viewWriter.removeAttribute('height', img);
            }
          } else {
            viewWriter.removeAttribute('width', img);
            viewWriter.removeStyle('width', img);
            viewWriter.removeStyle('height', img);
            viewWriter.removeStyle('aspect-ratio', img);
            viewWriter.removeAttribute('height', img);
          }
        }
      }
    }, { priority: 'high' });
  });
}


function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;

  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  return (
    "#" +
    [f(0), f(8), f(4)]
      .map((x) =>
        Math.round(255 * x)
          .toString(16)
          .padStart(2, "0")
      )
      .join("")
  );
}

function normalizeColor(value) {
  if (!value) return value;

  // hsl(...)
  if (value.startsWith("hsl")) {
    const [h, s, l] = value.match(/[\d.]+/g).map(Number);
    return hslToHex(h, s, l);
  }

  // rgb / rgba
  if (value.startsWith("rgb")) {
    const [r, g, b] = value.match(/[\d.]+/g).map(Number);
    return (
      "#" +
      [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")
    );
  }

  return value; // already hex
}

function forceHexColorPlugin(editor) {
  const commandNames = ["fontColor", "fontBackgroundColor"];

  commandNames.forEach((name) => {
    const command = editor.commands.get(name);
    if (!command) return;

    const originalExecute = command.execute.bind(command);

    command.execute = (options = {}) => {
      if (options.value) {
        options.value = normalizeColor(options.value);
      }
      return originalExecute(options);
    };
  });
}

export const editorConfig = {
  extraPlugins: [uploadPlugin, autoHeightImagePlugin,  forceHexColorPlugin, customImageResizePlugin],
  toolbar: {
    items: [
     "undo",
      "redo",
      "|",
      "heading",
      "|",
      "bold",
      "showBlocks",
      "italic",
      "underline",
      "bulletedList",
      "numberedList",
      // "fontSize",
      "fontColor",
      "fontBackgroundColor",
      "strikethrough",
      "fontFamily",
      "|",
      "imageInsert",
      // 'mediaEmbed',
      // "insertTable",
      // "insertTable",
      // "blockQuote",
      "superscript",
      // 'specialCharacters',
      "link",
      // 'codeBlock',
      "|",
      "alignment",
      "sourceEditing",
    ],
    shouldNotGroupWhenFull: false,
  },
  plugins: [
    AccessibilityHelp,
    Alignment,
    AutoImage,
    AutoLink,
    Autoformat,
    Autosave,
    // BlockQuote,
    ShowBlocks,
    Bold,
    CloudServices,
    Code,
    // CodeBlock,
    Essentials,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    // FontSize,
    GeneralHtmlSupport,
    Heading,
    ImageBlock,
    ImageCaption,
    ImageInline,
    ImageInsert,
    ImageResize,
    ImageStyle,
    ImageTextAlternative,
    ImageToolbar,
    ImageUpload,
    Indent,
    IndentBlock,
    Italic,
    Link,
    LinkImage,
    List,
    // MediaEmbed,
    Paragraph,
    PasteFromOffice,
    SelectAll,

    // SpecialCharacters,
    Strikethrough,
    Style,
    Superscript,
    // Table,
    // TableCaption,
    // TableCellProperties,
    // TableColumnResize,
    // TableProperties,
    // TableToolbar,
    // TextPartLanguage,
    TextTransformation,
    Underline,
    Undo,
    SourceEditing,
  ],
  balloonToolbar: [
    "bold",
    "italic",
    "|",
    "link",
    "insertImage",
    "|",
    "bulletedList",
    "numberedList",
  ],
  heading: {
    options: [
      {
        model: "paragraph",
        title: "Paragraph",
        class: "ck-heading_paragraph",
      },
      {
        model: "heading1",
        view: "h1",
        title: "Heading 1",
        class: "ck-heading_heading1",
      },
      {
        model: "heading2",
        view: "h2",
        title: "Heading 2",
        class: "ck-heading_heading2",
      },
      {
        model: "heading3",
        view: "h3",
        title: "Heading 3",
        class: "ck-heading_heading3",
      },
      {
        model: "heading4",
        view: "h4",
        title: "Heading 4",
        class: "ck-heading_heading4",
      },
      // {
      //   model: "heading5",
      //   view: "h5",
      //   title: "Heading 5",
      //   class: "ck-heading_heading5",
      // },
      // {
      //   model: "heading6",
      //   view: "h6",
      //   title: "Heading 6",
      //   class: "ck-heading_heading6",
      // },
    ],
  },

  image: {
    toolbar: [
      "toggleImageCaption",
      "imageTextAlternative",
      "|",
      "imageStyle:inline",
      "imageStyle:wrapText",
      "imageStyle:breakText",
      "|",
      "resizeImage",
    ],
  },
  contentStyles: [
    // Your default content styles + image styling
    "img { max-width: 100%; height: auto; display: block;}",
  ],
  initialData: "",
  link: {
    addTargetToExternalLinks: true,
    defaultProtocol: "https://",
    decorators: {
      toggleDownloadable: {
        mode: "manual",
        label: "Downloadable",
        attributes: {
          download: "file",
        },
      },
    },
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true,
    },
  },
  menuBar: {
    isVisible: true,
  },
  placeholder: "Type or paste your content here!",
  // table: {
  //   contentToolbar: [
  //     "tableColumn",
  //     "tableRow",
  //     "mergeTableCells",
  //     "tableProperties",
  //     "tableCellProperties",
  //   ],
  // },
  image: {
    resizeUnit: "px",
    resizeOptions: [
      { name: "resizeImage:original", label: "Original", value: null },
      { name: "resizeImage:50", label: "50%", value: "50" },
      { name: "resizeImage:75", label: "75%", value: "75" },
    ],
    toolbar: [
      "toggleImageCaption",
      "imageTextAlternative",
      "|",
      "imageStyle:inline",
      "imageStyle:wrapText",
      "imageStyle:breakText",
      "|",
      "resizeImage",
    ],
  },
  htmlSupport: {
    allow: [
      {
        name: /^.*$/,
        styles: true,
        attributes: true,
        classes: true,
      },
    ],
  },
  clipboard: {
    pasteAsPlainText: true, // forces plain text paste
  },
};

class MyCustomUploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }

  async upload() {
    try {
      const file = await this.loader.file;
      const response = await this._sendRequest(file);

      if (!response || response?.error) {
        window.alert(`Upload failed: ${response?.error?.message}`);
        throw new Error(
          response?.error ? response?.error?.message : "Upload failed."
        );
      }
      return {
        default: response?.location,
      };
    } catch (error) {
      window.alert(`Upload failed: ${error?.message}`);
      throw new Error(`Upload failed: ${error?.message}`);
    }
  }

  async _sendRequest(file) {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${APIUrl}/Common/CKEditorImageUpload`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Network response was not ok!!");
    }

    return await response.json();
  }
}

function uploadPlugin(editor) {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
    return new MyCustomUploadAdapter(loader);
  };
}

function autoHeightImagePlugin(editor) {
  editor.model.document.on("change:data", () => {
    editor.model.change((writer) => {
      const root = editor.model.document.getRoot();
      const range = editor.model.createRangeIn(root);
      for (const { item } of range.getWalker()) {
        if (item.name === "imageBlock" || item.name === "imageInline") {
          if (item.hasAttribute("height")) {
            writer.setAttribute("height", "auto", item);
          }
        }
      }
    });
  });
}